import type { FC } from "react";

import { SectionRole } from "../../../../shared/types/roles";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { useAppSelector } from "store";
import { useCapabilities } from "store/capability-hooks";
import {
  pageSelectors,
  selectCurrentSectionUserId,
  selectFullSubmissionById,
  selectPageAccessDataForSectionUser,
  selectSectionRoleBySectionUserId,
  selectUserBySectionUserId,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { isGroupPage } from "utils/page-utils";
import { useQueryParam } from "utils/routing";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

export type GroupSubmittedByBannerProps = {
  pageId: string;
};

export const GroupSubmittedByBanner: FC<GroupSubmittedByBannerProps> = ({ pageId }) => {
  const { canParticipateInSection } = useCapabilities();
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["interaction_mode", "submissions_last_collected_at"],
  );

  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  const currentSectionUserId = useAppSelector(selectCurrentSectionUserId);
  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForSectionUser(s, pageId, currentSectionUserId),
  );

  // For group pages, show who submitted the page for the group
  // If no submissions have been collected yet, we show who marked the page as complete
  const displayedUser = useObjectSelector(
    (s) =>
      selectUserBySectionUserId(s, submission?.submitted_by_id || pageAccessData?.completed_by_id),
    PROFILE_PROPERTIES,
  );

  const submitterRole = useAppSelector((s) =>
    selectSectionRoleBySectionUserId(s, submission?.submitted_by_id),
  );
  const isPageSubmittedByStudent = submitterRole === SectionRole.STUDENT;
  const isPageCompleted = Boolean(
    pageAccessData?.completed_at &&
      pageAccessData?.completed_by_id &&
      !page?.submissions_last_collected_at,
  );
  const showGroupSubmittedByBanner =
    canParticipateInSection && isGroupPage(page) && (isPageSubmittedByStudent || isPageCompleted);

  if (!page || !displayedUser || !showGroupSubmittedByBanner) {
    return null;
  }

  const fullName = getUserFullName(displayedUser);

  return (
    <Banner className="page-block-width" kind="info">
      {t("warning.group_submitted_by", { name: fullName })}
    </Banner>
  );
};
